import React from "react";
import { DateRangeProvider } from "../contexts/DateRangeContext";
import { IMerchant } from "../interfaces";
import {
  AcceptInvitation,
  AppCountryReports,
  AppDetail,
  AppList,
  Auth,
  BidManagement,
  Campaigns,
  Dashboard,
  ForgotPassword,
  IntegrationResolver,
  Integrations,
  Merchants,
  NotFound,
  OrdersList,
  ResetPassword,
  RoleForm,
  RoleManagement,
  SessionActivity,
  Settings,
  Support,
  TakeOverLogin,
  TeamManagement,
  UserActivity
} from "../routes/asyncComponents";

import {
  AndroidLogo,
  ClockUser,
  Globe,
  GoogleLogo,
  GooglePlayLogo,
  // Headset,
  Shield,
  SquaresFour,
  Users
} from "@phosphor-icons/react";
import { Navigate } from "react-router-dom";
import { IPermissionCategories } from "../features/Configurations/TeamManagement/interface";
import { PERMISSION_MAPPER } from "../shared/constants";

export interface RouteConfig {
  path: string;
  element?: React.ReactNode;
  protected?: boolean;
  isSuperAdminRoute?: boolean;
  permission?: string;
  label?: string;
  icon?: React.ReactNode;
  submenu?: RouteConfig[];
  excludeForCustomAdmin?: boolean;
}

export const routesConfig: RouteConfig[] = [
  // Public Routes

  {
    path: "/auth",
    element: <Auth />,
    protected: false,
  },
  {
    path: "/accept-invite",
    element: <AcceptInvitation />,
    protected: false,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
    protected: false,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
    protected: false,
  },
  {
    path: "/accept-invitation",
    element: <AcceptInvitation />,
    protected: false,
  },
  // Protected Routes
  {
    path: "/",
    element: <Navigate to="/dashboard" />,
    protected: true,
    permission: PERMISSION_MAPPER.DASHBOARD_READ,
  },
  {
    path: "/dashboard",
    element: (
      <DateRangeProvider>
        <Dashboard />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.DASHBOARD_READ,
    label: "common.dashboard",
    icon: <SquaresFour size={20} weight="duotone" />,
  },
  {
    path: "/app-country-reports",
    element: (
      <DateRangeProvider>
        <AppCountryReports />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.APP_COUNTRY_REPORTS_READ,
    label: "appCountryWiseReports.title",
    icon: <Globe size={20} weight="duotone" />,
  },
  {
    path: "/app-country-reports/:slug",
    element: (
      <DateRangeProvider>
        <AppDetail />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.APP_FIND_ONE,
  },
  {
    path: "/app-country-reports/:app-slug/bid-management",
    element: <BidManagement />,
    protected: true,
    permission: PERMISSION_MAPPER.BID_MANAGEMENT_READ,
  },
  {
    path: "/apps/:app-slug/bid-management",
    element: <BidManagement />,
    protected: true,
    permission: PERMISSION_MAPPER.BID_MANAGEMENT_READ,
  },
  {
    path: "/merchants",
    element: <Merchants />,
    protected: true,
    isSuperAdminRoute: true,
    label: "common.merchants",
    icon: <Users size={18} />,
  },
  {
    path: "/apps",
    element: (
      <DateRangeProvider>
        <AppList />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.APP_READ,
    label: "common.apps",
    icon: <AndroidLogo size={20} weight="duotone" />,
  },
  {
    path: "/apps/:slug",
    element: (
      <DateRangeProvider>
        <AppDetail />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.APP_FIND_ONE,
  },
  {
    path: "/orders",
    element: (
      <DateRangeProvider>
        <OrdersList />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.ORDER_READ,
    label: "orders.orderManagement",
    icon: <GooglePlayLogo size={20} weight="duotone" />,
  },
  {
    path: "/campaigns",
    element: <Campaigns />,
    protected: true,
    permission: PERMISSION_MAPPER.CAMPAIGN_READ,
    // label: "common.campaigns",
    // icon: <Megaphone size={18} />,
  },
  // {
  //   path: "/campaigns/:id",
  //   element: <CampaignDetail />,
  //   protected: true,
  //   permission: PERMISSION_MAPPER.CAMPAIGN_FIND_ONE,
  // },
  {
    path: "/settings",
    element: <Settings />,
    protected: true,
    isSuperAdminRoute: true,
    permission: "",
  },
  {
    path: "/support",
    element: <Support />,
    protected: true,
    permission: "",
    isSuperAdminRoute: true,
  },
  {
    path: "/integration-pairs",
    element: <Integrations />,
    protected: true,
    permission: PERMISSION_MAPPER.INTEGRATION_READ,
    label: "common.integrations",
    icon: <GoogleLogo size={20} weight="duotone" />,
  },
  // {
  //   path: "/configurations/integrations",
  //   element: <Integrations />,
  //   protected: true,
  //   permission: PERMISSION_MAPPER.INTEGRATION_READ,
  // },

  {
    path: "/integrations",
    element: <IntegrationResolver />,
    protected: true,
    permission: PERMISSION_MAPPER.INTEGRATION_READ,
  },
  // {
  //   path: "/integrations/add",
  //   element: <IntegrationResolver />,
  //   protected: true,
  //   permission: PERMISSION_MAPPER.INTEGRATION_CREATE,
  // },
  // {
  //   path: "/integrations/:trackingId",
  //   element: <IntegrationResolver />,
  //   protected: true,
  //   permission: PERMISSION_MAPPER.INTEGRATION_CREATE,
  // },
  // {
  //   path: "/integrations/update",
  //   element: <IntegrationResolver />,
  //   protected: true,
  //   permission: PERMISSION_MAPPER.INTEGRATION_UPDATE,
  // },
  {
    path: "/team-management",
    element: <TeamManagement />,
    protected: true,
    permission: PERMISSION_MAPPER.TEAM_MANAGEMENT_READ,
    label: "configurations.team.title",
    icon: <Users size={20} weight="duotone" />,
  },
  {
    path: "/role-management",
    element: <RoleManagement />,
    protected: true,
    permission: PERMISSION_MAPPER.ROLE_MANAGEMENT_READ,
    label: "configurations.roles.title",
    icon: <Shield size={20} weight="duotone" />,
    excludeForCustomAdmin: true,
  },
  {
    path: "/role-management/create",
    element: <RoleForm />,
    protected: true,
    permission: PERMISSION_MAPPER.ROLE_MANAGEMENT_CREATE,
    excludeForCustomAdmin: true,
  },
  {
    path: "/role-management/:id",
    element: <RoleForm />,
    protected: true,
    permission: PERMISSION_MAPPER.ROLE_MANAGEMENT_UPDATE,
    excludeForCustomAdmin: true,
  },
  {
    path: "/user-activity",
    element: <UserActivity />,
    protected: true,
    permission: "",
    label: "userActivity.title",
    icon: <ClockUser size={20} weight="duotone" />,
  },
  {
    path: "/sessions-activity",
    element: <SessionActivity />,
    protected: true,
    permission: "SESSION_ACTIVITY_READ",
  },
  // {
  //   path: "/activities",
  //   element: <Ghost />,
  //   protected: true,
  //   permission: "",
  //   label: "common.activities",
  //   icon: <SquaresFour size={20} weight="duotone" />,
  //   submenu: [
  //     {
  //       path: "/activities/user-activity",
  //       element: <UserActivity />,
  //       protected: true,
  //       permission: "",
  //       label: "userActivity.title",
  //       icon: <MapPinSimple size={20} weight="duotone" />,
  //     },
  //     {
  //       path: "/activities/sessions-activity",
  //       element: <SessionActivity />,
  //       protected: true,
  //       permission: "",
  //       label: "configurations.team.sessions.button",
  //       icon: <ClockUser size={20} weight="duotone" />,
  //     },
  //   ],
  // },
  {
    path: "/take-over-login",
    element: <TakeOverLogin />,
    protected: false,
  },
  // {
  //   path: "/configurations",
  //   element: <Ghost />,
  //   protected: true,
  //   allowedRoles: ["admin", "sub_admin"],
  //   label: "common.configurations",
  //   icon: <SettingsIcon size={18} />,
  //   submenu: [
  //     {
  //       path: "/configurations/integrations",
  //       element: <Integrations />,
  //       protected: true,
  //       allowedRoles: ["admin", "sub_admin"],
  //       label: "common.integrations",
  //       icon: <Puzzle size={18} />,
  //     },
  //     {
  //       path: "/integrations/add",
  //       element: <AddIntegration />,
  //       protected: true,
  //       allowedRoles: ["admin", "sub_admin"],
  //     },
  //     {
  //       path: "/configurations/team-management",
  //       element: <TeamManagement />,
  //       protected: true,
  //       allowedRoles: ["admin"],
  //       label: "configurations.team.title",
  //       icon: <User size={18} />,
  //     },
  //     {
  //       path: "/configurations/role-management",
  //       element: <RoleManagement />,
  //       protected: true,
  //       allowedRoles: ["admin"],
  //       label: "configurations.roles.title",
  //       icon: <Shield size={18} />,
  //     },
  //   ],
  // },
  {
    path: "/404",
    element: <NotFound />,
    protected: false,
  },
];

// Helper functions to get specific route configurations
export const getPublicRoutes = () =>
  routesConfig.filter((route) => !route.protected);

export const getProtectedRoutes = (isSuperAdmin: boolean) => {
  const flattenRoutes = (routes: RouteConfig[]): RouteConfig[] => {
    return routes.reduce((acc: RouteConfig[], route) => {
      acc.push(route);
      if (route.submenu) {
        acc.push(...flattenRoutes(route.submenu));
      }
      return acc;
    }, []);
  };

  return flattenRoutes(
    routesConfig.filter((route) =>
      isSuperAdmin
        ? route.protected && route.isSuperAdminRoute
        : route.protected
    )
  );
};

export const getNavItems = (user: IMerchant | null) => {
  if (!user) return [];
  const permittedRoutes = getPermittedRoutes(user);
  return permittedRoutes
    .filter((route) => route.label && route.icon)
    .map((route) => {
      if (route.submenu) {
        const filteredSubmenu = route.submenu.filter(
          (sub) => sub.label && sub.icon
        );
        if (filteredSubmenu.length === 0) {
          delete route.submenu;
        } else {
          route.submenu = filteredSubmenu;
        }
      }
      return route;
    });
};

function filterRoutesByPermission(
  routes: RouteConfig[],
  user: IMerchant
): RouteConfig[] {
  return routes
    .map((route) => {
      let permittedSubmenu: RouteConfig[] | undefined;
      if (route.submenu) {
        permittedSubmenu = filterRoutesByPermission(route.submenu, user);
      }

      // Check permission for this route
      const hasPermission =
        !route.permission ||
        ((user.user_permissions as IPermissionCategories)?.modules || []).some(
          (module) => module.codename === route.permission
        );

      // Include this route if:
      // - user has permission for this route, or
      // - there are permitted submenus
      if (hasPermission || (permittedSubmenu && permittedSubmenu.length > 0)) {
        const newRoute = { ...route };
        if (permittedSubmenu && permittedSubmenu.length > 0) {
          newRoute.submenu = permittedSubmenu;
        } else {
          delete newRoute.submenu;
        }
        return newRoute;
      }
      return null;
    })
    .filter(Boolean) as RouteConfig[];
}

export const getPermittedRoutes = (user: IMerchant | null) => {
  if (!user) return [];
  if (user.is_superuser) {
    return routesConfig.filter(
      (route) => route.isSuperAdminRoute && route.protected
    );
  }
  if (user.is_team_admin) {
    return routesConfig.filter(
      (route) => route.protected && !route.isSuperAdminRoute
    );
  } else if (
    !user.is_team_admin &&
    user.user_permissions === PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
  ) {
    return routesConfig.filter(
      (route) => route.protected && !route.isSuperAdminRoute
    );
  }

  // Use the recursive filter for normal users
  return filterRoutesByPermission(
    routesConfig.filter((route) => route.protected && !route.isSuperAdminRoute),
    user
  );
};
